import {
  Article,
  ArticleAdditionalFeedbackTypeNegative,
} from '@wix/answers-api';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';

import { CHATBOT_REFERRALS } from '../../../../constants';
import { DATA_HOOKS } from '../../../../dataHooks';
import { useArticlePageBi } from '../../../../utils/article-page-bi';
import { useStore } from '../../../../zustand/stores';
import { ChatbotFlow } from '../../../../zustand/stores/copilot';
import { MessageSender } from '../MessageSender';

import css from './index.module.scss';

type UnhelpfulFeedbackOptionsProps = {
  unhelpfulFeedbackOptionSelected: ArticleAdditionalFeedbackTypeNegative | null;
  setUnhelpfulFeedbackOptionSelected: (
    unhelpfulFeedbackOption: ArticleAdditionalFeedbackTypeNegative
  ) => void;
  article: Article;
  sendMessage: (message: string, forceReferral?: string) => void;
};

export const UnhelpfulFeedbackOptions = ({
  unhelpfulFeedbackOptionSelected,
  setUnhelpfulFeedbackOptionSelected,
  article,
  sendMessage,
}: UnhelpfulFeedbackOptionsProps) => {
  const { t } = useTranslation();
  const { unhelpfulFeedbackTrack } = useArticlePageBi(article);
  const { setChatbotReferral, chatbotFlow } = useStore(
    (state) => state.copilotSlice
  );

  const onOptionClick = async (
    unhelpfulFeedbackOption: ArticleAdditionalFeedbackTypeNegative
  ) => {
    setUnhelpfulFeedbackOptionSelected(unhelpfulFeedbackOption);

    const clickedText = t(
      `article-feedback.additional.unhelpful.option.${unhelpfulFeedbackOption}`
    );
    const optionValues = Object.values(
      ArticleAdditionalFeedbackTypeNegative
    ).filter((value) => typeof value === 'number') as number[];
    const clickedItemOrder = (
      optionValues.indexOf(unhelpfulFeedbackOption) + 1
    ).toString();

    await unhelpfulFeedbackTrack('', clickedText, clickedItemOrder);

    if (
      unhelpfulFeedbackOption !== ArticleAdditionalFeedbackTypeNegative.OTHER
    ) {
      setChatbotReferral(CHATBOT_REFERRALS.UNHELPFUL_ARTICLE);
      if (chatbotFlow === ChatbotFlow.REGULAR) {
        sendMessage(clickedText, CHATBOT_REFERRALS.UNHELPFUL_ARTICLE);
      }
    }
  };

  return (
    <Box
      gap={1}
      direction="vertical"
      dataHook={DATA_HOOKS.UNHELPFUL_OPTIONS}
      className={css.unhelpfulFeedbackOptions}
    >
      <MessageSender />
      {Object.values(ArticleAdditionalFeedbackTypeNegative)
        .filter((value) => typeof value === 'number')
        .map((unhelpfulFeedbackOption) => (
          <div
            onClick={() => onOptionClick(unhelpfulFeedbackOption)}
            key={unhelpfulFeedbackOption}
            data-hook={DATA_HOOKS.UNHELPFUL_FEEDBACK_OPTION}
          >
            <Box
              className={classNames(css.negativeFeedbackOption, {
                [css.negativeFeedbackOptionSelected]:
                  unhelpfulFeedbackOptionSelected === unhelpfulFeedbackOption,
              })}
            >
              <Text size="small">
                {t(
                  `article-feedback.additional.unhelpful.option.${unhelpfulFeedbackOption}`
                )}
              </Text>
            </Box>
          </div>
        ))}
    </Box>
  );
};
